/* eslint-disable no-console */
import { useEffect, useRef, useState } from "react";

import { registerServiceWorker } from "Helpers/registerServiceWorker";

const SW_UPDATE_CHECK_INTERVAL_MS = 300000;
const SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY = "service-worker-update-activation-in-progress";
const SW_SUPPRESS_WAITING_PROMPT_AFTER_RELOAD_STORAGE_KEY = "service-worker-suppress-waiting-prompt-after-reload";
const SW_RELOAD_TRIGGERED_STORAGE_KEY = "service-worker-reload-triggered";

type UseServiceWorkerReloadPromptResult = {
    confirmReloadPrompt: () => void;
    showReloadPrompt: boolean;
};

const getStoredFlag = (storageKey: string) => {
    try {
        const value = window.sessionStorage.getItem(storageKey);
        return value === "true";
    } catch (error) {
        console.error("Service worker session flag read failed", { storageKey, error });
        return false;
    }
};

const setStoredFlag = (storageKey: string) => {
    try {
        window.sessionStorage.setItem(storageKey, "true");
    } catch (error) {
        console.error("Service worker session flag set failed", { storageKey, error });
    }
};

const clearStoredFlag = (storageKey: string) => {
    try {
        window.sessionStorage.removeItem(storageKey);
    } catch (error) {
        console.error("Service worker session flag clear failed", { storageKey, error });
    }
};

export const useServiceWorkerReloadPrompt = (): UseServiceWorkerReloadPromptResult => {
    const [showReloadPrompt, setShowReloadPrompt] = useState(false);
    const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);
    const isReloadPromptOpenRef = useRef(false);
    const isServiceWorkerReloadingRef = useRef(false);
    const hasHandledWaitingWorkerRef = useRef(false);

    useEffect(() => {
        isReloadPromptOpenRef.current = showReloadPrompt;
    }, [showReloadPrompt]);

    useEffect(() => {
        const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
        if (!sw) return;

        // Handle the case where we've reloaded after activating a new SW.
        // Do this before any waiting-worker checks can fire.
        if (sw.controller && getStoredFlag(SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY)) {
            clearStoredFlag(SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY);
            clearStoredFlag(SW_RELOAD_TRIGGERED_STORAGE_KEY);
            setStoredFlag(SW_SUPPRESS_WAITING_PROMPT_AFTER_RELOAD_STORAGE_KEY);
        }

        let isDisposed = false;
        let waitingWorkerCheckTimeoutId: number | null = null;
        const cleanupCallbacks: Array<() => void> = [];

        const clearUpdateFlowIfActivated = () => {
            if (!sw.controller) return;
            if (!getStoredFlag(SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY)) return;

            clearStoredFlag(SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY);
            clearStoredFlag(SW_RELOAD_TRIGGERED_STORAGE_KEY);
            setStoredFlag(SW_SUPPRESS_WAITING_PROMPT_AFTER_RELOAD_STORAGE_KEY);
        };

        const showPromptFor = (worker: ServiceWorker | null) => {
            if (!worker) return;
            if (isServiceWorkerReloadingRef.current || isReloadPromptOpenRef.current) return;

            if (getStoredFlag(SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY)) return;
            if (getStoredFlag(SW_SUPPRESS_WAITING_PROMPT_AFTER_RELOAD_STORAGE_KEY)) return;
            if (hasHandledWaitingWorkerRef.current) return;

            hasHandledWaitingWorkerRef.current = true;
            setShowReloadPrompt(true);
        };

        const checkForWaitingWorker = () => {
            const waitingWorker = swRegistrationRef.current?.waiting ?? null;

            if (!waitingWorker) {
                hasHandledWaitingWorkerRef.current = false;
                return;
            }

            showPromptFor(waitingWorker);
        };

        const scheduleWaitingWorkerCheck = () => {
            if (waitingWorkerCheckTimeoutId !== null) {
                window.clearTimeout(waitingWorkerCheckTimeoutId);
            }

            waitingWorkerCheckTimeoutId = window.setTimeout(() => {
                waitingWorkerCheckTimeoutId = null;
                checkForWaitingWorker();
            }, 0);
        };

        const checkForSwUpdate = () => {
            const reg = swRegistrationRef.current;

            if (!reg) {
                return;
            }

            reg.update()
                .then(() => {
                    console.info("Service worker update check completed", {
                        active: reg.active?.state ?? null,
                        installing: reg.installing?.state ?? null,
                        waiting: reg.waiting?.state ?? null,
                        controller: navigator.serviceWorker.controller?.scriptURL,
                        activeScript: reg.active?.scriptURL,
                        waitingScript: reg.waiting?.scriptURL,
                    });
                    checkForWaitingWorker();
                })
                .catch(error => {
                    console.error("Service worker update check failed", error);
                    checkForWaitingWorker();
                });
        };

        const checkForSwUpdateWhenVisible = () => {
            if (document.visibilityState === "visible") {
                checkForSwUpdate();
            }
        };

        const checkForSwUpdateWhenOnline = () => {
            if (navigator.onLine) {
                checkForSwUpdate();
            }
        };

        const onControllerChange = () => {
            if (!getStoredFlag(SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY)) {
                return;
            }

            if (getStoredFlag(SW_RELOAD_TRIGGERED_STORAGE_KEY)) {
                return;
            }

            setStoredFlag(SW_RELOAD_TRIGGERED_STORAGE_KEY);
            window.location.reload();
        };

        sw.addEventListener("controllerchange", onControllerChange);
        cleanupCallbacks.push(() => sw.removeEventListener("controllerchange", onControllerChange));

        registerServiceWorker(reg => {
            if (isDisposed) return;

            swRegistrationRef.current = reg;
            clearUpdateFlowIfActivated();
            checkForWaitingWorker();

            const onUpdateFound = () => {
                // New update discovered, allow prompts again.
                clearStoredFlag(SW_SUPPRESS_WAITING_PROMPT_AFTER_RELOAD_STORAGE_KEY);

                const newWorker = reg.installing;
                if (!newWorker) return;

                const onStateChange = () => {
                    if (newWorker.state === "installed" && sw.controller) {
                        scheduleWaitingWorkerCheck();
                    }
                };

                newWorker.addEventListener("statechange", onStateChange);
                cleanupCallbacks.push(() => newWorker.removeEventListener("statechange", onStateChange));
            };

            reg.addEventListener("updatefound", onUpdateFound);
            cleanupCallbacks.push(() => reg.removeEventListener("updatefound", onUpdateFound));

            checkForSwUpdate();
        });

        const intervalId = window.setInterval(checkForSwUpdateWhenVisible, SW_UPDATE_CHECK_INTERVAL_MS);
        window.addEventListener("focus", checkForSwUpdate);
        window.addEventListener("online", checkForSwUpdateWhenOnline);
        document.addEventListener("visibilitychange", checkForSwUpdateWhenVisible);

        cleanupCallbacks.push(() => window.clearInterval(intervalId));
        cleanupCallbacks.push(() => window.removeEventListener("focus", checkForSwUpdate));
        cleanupCallbacks.push(() => window.removeEventListener("online", checkForSwUpdateWhenOnline));
        cleanupCallbacks.push(() => document.removeEventListener("visibilitychange", checkForSwUpdateWhenVisible));
        cleanupCallbacks.push(() => {
            if (waitingWorkerCheckTimeoutId !== null) {
                window.clearTimeout(waitingWorkerCheckTimeoutId);
            }
        });

        return () => {
            isDisposed = true;
            cleanupCallbacks.forEach(cleanup => cleanup());
        };
    }, []);

    const confirmReloadPrompt = () => {
        isServiceWorkerReloadingRef.current = true;
        const waitingWorker = swRegistrationRef.current?.waiting ?? null;

        if (waitingWorker) {
            setStoredFlag(SW_UPDATE_ACTIVATION_IN_PROGRESS_STORAGE_KEY);
            waitingWorker.postMessage("SKIP_WAITING");
        }

        setShowReloadPrompt(false);
    };

    return {
        confirmReloadPrompt,
        showReloadPrompt,
    };
};
